import ReactECharts from 'echarts-for-react'
import { format } from 'date-fns'
import type { CallCountItem, ConnectivityItem } from '@/hooks/useRealtime'

interface RealTimeChartsProps {
  totalCalls: CallCountItem[]
  successfulCalls: CallCountItem[]
  connectedCalls: ConnectivityItem[]
  callsOver30s: CallCountItem[]
}

const chartThemes = [
  { line: '#6B8DB2', fill: 'rgba(107, 141, 178, 0.20)', fillEnd: 'rgba(107, 141, 178, 0.04)' },
  { line: '#5A9E6F', fill: 'rgba(90, 158, 111, 0.20)', fillEnd: 'rgba(90, 158, 111, 0.04)' },
  { line: '#9B7FB8', fill: 'rgba(155, 127, 184, 0.20)', fillEnd: 'rgba(155, 127, 184, 0.04)' },
  { line: '#C4915E', fill: 'rgba(196, 145, 94, 0.20)', fillEnd: 'rgba(196, 145, 94, 0.04)' },
]

function makeAreaStyle(theme: (typeof chartThemes)[number]) {
  return {
    color: {
      type: 'linear' as const,
      x: 0, y: 0, x2: 0, y2: 1,
      colorStops: [
        { offset: 0, color: theme.fill },
        { offset: 1, color: theme.fillEnd },
      ],
    },
  }
}

function makeChartOption(
  dates: string[],
  fullDates: string[],
  seriesName: string,
  data: number[],
  theme: (typeof chartThemes)[number],
  yAxisConfig?: Record<string, unknown>,
  valueFormatter?: (v: number) => string,
) {
  return {
    tooltip: {
      trigger: 'axis' as const,
      axisPointer: {
        type: 'cross' as const,
        lineStyle: { color: '#666', width: 1, type: 'dashed' as const },
      },
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#ddd',
      borderWidth: 1,
      textStyle: { color: '#333', fontSize: 13 },
      padding: 12,
      formatter: (params: any) => {
        const idx = params[0].dataIndex
        const val = valueFormatter ? valueFormatter(params[0].value) : params[0].value.toLocaleString()
        return `<b>${fullDates[idx]}</b><br/>${params[0].seriesName}: <b>${val}</b>`
      },
    },
    xAxis: {
      type: 'category' as const,
      data: dates,
      axisLabel: { rotate: 45, fontSize: 10 },
    },
    yAxis: { type: 'value' as const, ...yAxisConfig },
    grid: { left: '3%', right: '4%', bottom: '12%', top: '10%', containLabel: true },
    series: [{
      name: seriesName,
      type: 'line' as const,
      data,
      smooth: 0.3,
      lineStyle: { color: theme.line, width: 2.5 },
      areaStyle: makeAreaStyle(theme),
      itemStyle: { color: theme.line },
    }],
  }
}

export function RealTimeCharts({
  totalCalls,
  successfulCalls,
  connectedCalls,
  callsOver30s,
}: RealTimeChartsProps) {
  if (!totalCalls?.length || !successfulCalls?.length || !connectedCalls?.length || !callsOver30s?.length) {
    return <div className="text-center text-muted-foreground py-8">No data available</div>
  }

  const dates = totalCalls.map((item) => format(new Date(item.interval_start), 'MMM dd HH:mm'))
  const fullDates = totalCalls.map((item) => format(new Date(item.interval_start), 'MMM dd, yyyy HH:mm'))

  const totalCallsData = totalCalls.map((item) => Number(item.calls))
  const successfulCallsData = successfulCalls.map((item) => Number(item.calls))
  const connectivityRates = connectedCalls.map((item) => Number(item.connectivity_rate))
  const shareOver30s = totalCallsData.map((total, idx) =>
    total > 0 ? (Number(callsOver30s[idx]?.calls ?? 0) / total) * 100 : 0
  )

  const pctAxis = { min: 0, max: 100 }
  const pctFmt = (v: number) => `${v.toFixed(2)}%`

  const charts = [
    { title: 'Total Calls', option: makeChartOption(dates, fullDates, 'Total Calls', totalCallsData, chartThemes[0]) },
    { title: 'Successful Calls', option: makeChartOption(dates, fullDates, 'Successful Calls', successfulCallsData, chartThemes[1]) },
    { title: 'Connectivity Rate (%)', option: makeChartOption(dates, fullDates, 'Connectivity Rate', connectivityRates, chartThemes[2], pctAxis, pctFmt) },
    { title: 'Share of Calls Over 30s (%)', option: makeChartOption(dates, fullDates, 'Share Over 30s', shareOver30s, chartThemes[3], pctAxis, pctFmt) },
  ]

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
      {charts.map((chart) => (
        <div key={chart.title} className="bg-white rounded-lg shadow-sm p-6 border">
          <h3 className="text-center text-body font-semibold mb-4">{chart.title}</h3>
          <ReactECharts option={chart.option} style={{ height: '300px' }} />
        </div>
      ))}
    </div>
  )
}
